import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  drawingBaseProviders,
  LayersService,
  ObjectMetadata,
  objectsProviders,
  PlacedObject,
  ToolsService,
} from '@bcl/drawing-control';
import {
  Area,
  LocationDetailsOptions,
  Monitor,
  MonitorWithOptions,
} from '../area/area.model';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { AreaApiService } from '../area/area.service';
import { DictionaryService } from 'src/app/services/dictionary.service';
import { PreviewToolId, previewToolProvider } from './tools/preview.tool';
import { EditToolId, editToolProvider } from './tools/edit.tool';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectOption, SnackbarService, uiDebounce } from '@bcl/design-system';
import { Platform, ViewWillLeave } from '@ionic/angular';
import { NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AddMonitorDialogComponent } from './components/add-monitor-dialog/add-monitor-dialog.component';
import { AreaContractPart, AreaMapApiService } from './area-map-api.service';
import {
  AddMonitorDialogData,
  AddMonitorDialogResponse,
} from './components/add-monitor-dialog/add-monitor-dialog.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  AddMapActionDialogData,
  AddMapActionDialogResponse,
} from './components/add-map-action-dialog/add-map-action-dialog.model';
import { AddMapActionDialogComponent } from './components/add-map-action-dialog/add-map-action-dialog.component';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';
import { DataProviderService } from 'src/app/services/data-provider.service';
import { VisitService } from 'src/app/services/visit.service';
import { BackButtonService } from 'src/app/services/back-button.service';
import { areaMonitorsSvg } from './area-monitors';
import {
  AreaMapObjectsService,
  mapMonitorsToMapObjects,
  MonitorMapObject,
} from './area-map-objects';

type AreaObject = PlacedObject<string, Area>;

export type MonitorsPositionChanged = {
  areaId: Area['id'];
  positions: Array<Pick<Monitor, 'id' | 'coordinates' | 'type'>>;
};

@Component({
  selector: 'app-area-map',
  templateUrl: './area-map.component.html',
  styleUrls: ['./area-map.component.scss'],
  providers: [
    drawingBaseProviders,
    objectsProviders,
    previewToolProvider,
    editToolProvider,
    AreaMapObjectsService,
  ],
})
export class AreaMapPage implements OnInit, OnDestroy, ViewWillLeave {
  @ViewChild('mapContainer', { static: false }) mapContainer: any;
  @ViewChild('areaDetailsMonitors') areaDetailsMonitors: any;
  public form: FormGroup;
  public loading = true;
  public hasError = false;
  public visitId: number;
  public date: string;
  public dayNumber: string;
  public areaId: number;
  public locationId: number;
  public customerId: number;
  public selectedAreaData?: Area;
  public mapParts: AreaObject[] = [];
  public options: LocationDetailsOptions = {
    location: {
      technicians: [],
      skills: [],
      evaluationQuestions: [],
      days: [],
    },
    area: { types: [], subtypes: [], certifications: [] },
    monitor: {
      modules: [],
      types: [],
      statuses: [],
      product: { products: [], units: [], warehouses: [] },
      groups: [],
    },
  };
  public moduleOptions: SelectOption[] = [];
  public instrumentTypeOptions: SelectOption[] = [];
  public selectedArea?: Area;
  public mapMonitors: MonitorMapObject[] = [];
  public legend: {
    label: string;
    svg: { content: SafeHtml; size: { width: number; height: number } };
  }[] = [];
  public mapMonitorsSvg = areaMonitorsSvg;
  public showLegend = false;
  public showMap = true;
  public showFilters = false;
  public areaParts: AreaContractPart[] = [];
  public defaultCar: number | null = null;
  public visit?: any;
  public isEditingDisabled = false;

  public mappedMonitorsForList: MonitorWithOptions[] = [];

  public tool = PreviewToolId;
  public monitorsPlacerSettingsId: string | undefined;

  private changedMonitorsPosition?: MonitorsPositionChanged['positions'];
  private backButtonSubscription: any;

  private touchStartTime = 0;
  private touchStartPosition = { x: 0, y: 0 };
  private touchEndPosition = { x: 0, y: 0 };
  private readonly swipeThreshold = 5;
  private isSwiping = false;
  private hasMoved = false;
  private isTouching = false;
  private blockClicks = false;
  private touchStartedOnMonitor = false;
  private pendingClick: { object: ObjectMetadata; timestamp: number } | null =
    null;
  private isMultiTouch = false;
  private touchStartHandler: (event: TouchEvent) => void;
  private touchMoveHandler: (event: TouchEvent) => void;
  private touchEndHandler: (event: TouchEvent) => void;
  private allVisitsForDay: any[] = [];
  private pendingTimeouts: NodeJS.Timeout[] = [];

  private readonly destroyed$ = new Subject<void>();

  constructor(
    fb: FormBuilder,
    private toolsService: ToolsService,
    private domSanitizer: DomSanitizer,
    private areaApiService: AreaApiService,
    private dictionaryService: DictionaryService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private platform: Platform,
    private dialog: MatDialog,
    private areaMapService: AreaMapApiService,
    private router: Router,
    private stateService: StateService,
    private storageService: StorageService,
    private dataService: DataProviderService,
    private visitService: VisitService,
    private backButtonService: BackButtonService,
    private ngZone: NgZone,
    private areaMapObjectsService: AreaMapObjectsService
  ) {
    this.form = fb.group({
      moduleId: [{ value: '' }],
      instrumentType: [{ value: '' }],
      toxic: [{ value: 'all' }],
      temporary: [{ value: 'all' }],
      outside: [{ value: 'all' }],
      showUnscannedOnly: [{ value: false }],
      checkStatus: [{ value: 'all' }],
    });
  }

  get isFormDisabled(): boolean {
    return this.isEditingDisabled;
  }

  ionViewWillEnter(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
    this.toolsService.useTool(this.tool);
    this.platform.ready().then(() => {
      this.setupTouchHandling();
    });
  }

  ionViewWillLeave(): void {
    this.cleanupTouchHandlers();
  }

  ngOnInit(): void {
    this.toolsService.setGroup(LayersService.baseLayerId);

    this.form.valueChanges
      .pipe(uiDebounce(), takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (this.form.valid) {
          localStorage.setItem(`moduleId_${this.areaId}`, value.moduleId || '');
          localStorage.setItem(
            `instrumentType_${this.areaId}`,
            value.instrumentType || ''
          );
          const toxicValue = ['all', 'yes', 'no'].includes(value.toxic)
            ? value.toxic
            : 'all';
          const temporaryValue = ['all', 'yes', 'no'].includes(value.temporary)
            ? value.temporary
            : 'all';
          const outsideValue = ['all', 'yes', 'no'].includes(value.outside)
            ? value.outside
            : 'all';
          const checkStatusValue = ['all', 'yes', 'no'].includes(
            value.checkStatus
          )
            ? value.checkStatus
            : 'all';

          localStorage.setItem(`toxicFilter_${this.areaId}`, toxicValue);
          localStorage.setItem(
            `temporaryFilter_${this.areaId}`,
            temporaryValue
          );
          localStorage.setItem(`outsideFilter_${this.areaId}`, outsideValue);
          localStorage.setItem(
            `unscannedOnly_${this.areaId}`,
            value.showUnscannedOnly?.toString() || 'false'
          );
          localStorage.setItem(
            `checkStatusFilter_${this.areaId}`,
            checkStatusValue
          );

          this.filterMonitors(
            value.moduleId,
            value.instrumentType,
            toxicValue,
            temporaryValue,
            outsideValue,
            value.showUnscannedOnly,
            checkStatusValue
          );

          if (
            this.areaDetailsMonitors &&
            this.areaDetailsMonitors.updateExternalFilters
          ) {
            this.areaDetailsMonitors.updateExternalFilters({
              moduleId: value.moduleId,
              instrumentType: value.instrumentType,
              toxic: toxicValue,
              temporary: temporaryValue,
              outside: outsideValue,
              showUnscannedOnly: value.showUnscannedOnly,
              checkStatus: checkStatusValue,
            });
          }
        }
      });

    this.backButtonSubscription =
      this.platform.backButton.subscribeWithPriority(10, () => {
        this.dialog.closeAll();
        this.goBack();
      });

    this.route.params.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      this.visitId = params?.id;
      this.date = params?.date;
      this.dayNumber = params?.dayId;
      this.areaId = params?.areaid;
      this.monitorsPlacerSettingsId = `visit-${this.visitId}-area-${this.areaId}-monitors`;

      this.loadSelectedCarForToday();
      combineLatest({
        options: this.areaApiService.getLocationDetailsOptions(this.areaId),
        groups: this.dictionaryService
          .getDictionaryItemsByKey('PC.INSTRUMENT.TYPE')
          .pipe(
            map((items) =>
              items.map((item) => ({
                ...item,
                additionalValues: item.additionalValues
                  ? JSON.parse(item.additionalValues)
                  : null,
              }))
            )
          ),
        area: this.areaApiService.getArea(this.areaId, this.visitId),
        areaMonitors: this.areaApiService.getMonitors(
          this.areaId,
          this.visitId,
          {
            status: null,
            module: null,
            type: null,
          }
        ),
        areaParts: this.areaMapService.getPartsForArea(
          this.areaId,
          this.visitId
        ),
        visit: this.dataService.getEljakimJob(this.visitId.toString(), true),
        allVisitsForDay: this.dataService.getEljakimJobs(this.date),
      })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: ({
            options,
            groups,
            area,
            areaMonitors,
            areaParts,
            visit,
            allVisitsForDay,
          }) => {
            if (!area) {
              console.error('Area data not found');
              this.handleDataLoadError();
              return;
            }

            this.options = {
              ...options,
              monitor: {
                ...options.monitor,
                groups,
              },
            };
            this.selectedArea = area;
            this.selectedArea.monitors = areaMonitors;
            this.locationId = this.selectedArea.locationId;
            this.customerId = this.selectedArea.customerId;
            this.visit = visit;
            this.allVisitsForDay = allVisitsForDay;
            this.hasError = false;
            this.updateDisabledStates();

            this.mapMonitors = mapMonitorsToMapObjects(
              this.selectedArea?.monitors
            );

            this.mapParts = this.selectedAreaData?.mapPart
              ? [
                  {
                    id: this.selectedAreaData.id.toString(),
                    type: 'area',
                    coordinates: {
                      x: this.selectedAreaData.mapPart.x,
                      y: this.selectedAreaData.mapPart.y,
                    },
                    data: this.selectedAreaData,
                  },
                ]
              : [];
            this.moduleOptions = this.filterModuleOptions(
              this.selectedArea?.monitors,
              this.options.monitor.modules
            );
            this.moduleOptions.unshift({ label: 'All', value: '' });

            this.instrumentTypeOptions = this.filterInstrumentTypeOptions(
              this.selectedArea?.monitors,
              this.options.monitor.types
            );
            this.instrumentTypeOptions.unshift({ label: 'All', value: '' });

            const savedModuleId = localStorage.getItem(
              `moduleId_${this.areaId}`
            );
            const savedInstrumentType = localStorage.getItem(
              `instrumentType_${this.areaId}`
            );
            const savedToxic = localStorage.getItem(
              `toxicFilter_${this.areaId}`
            );
            const savedTemporary = localStorage.getItem(
              `temporaryFilter_${this.areaId}`
            );
            const savedOutside = localStorage.getItem(
              `outsideFilter_${this.areaId}`
            );
            const savedCheckStatus = localStorage.getItem(
              `checkStatusFilter_${this.areaId}`
            );
            const savedShowUnscannedOnly = localStorage.getItem(
              `unscannedOnly_${this.areaId}`
            );

            const validModuleId =
              savedModuleId &&
              this.moduleOptions.some((opt) => opt.value === savedModuleId)
                ? savedModuleId
                : '';
            const validInstrumentType =
              savedInstrumentType &&
              this.instrumentTypeOptions.some(
                (opt) => opt.value === savedInstrumentType
              )
                ? savedInstrumentType
                : '';

            const validToxic = ['all', 'yes', 'no'].includes(savedToxic)
              ? savedToxic
              : 'all';
            const validTemporary = ['all', 'yes', 'no'].includes(savedTemporary)
              ? savedTemporary
              : 'all';
            const validOutside = ['all', 'yes', 'no'].includes(savedOutside)
              ? savedOutside
              : 'all';
            const validCheckStatus = ['all', 'yes', 'no'].includes(
              savedCheckStatus
            )
              ? savedCheckStatus
              : 'all';

            this.form.patchValue({
              moduleId: validModuleId,
              instrumentType: validInstrumentType,
              toxic: validToxic,
              temporary: validTemporary,
              outside: validOutside,
              showUnscannedOnly: savedShowUnscannedOnly === 'true',
              checkStatus: validCheckStatus,
            });

            this.areaParts = areaParts;
            this.loading = false;
          },
          error: (error) => {
            console.error('Error loading area map data:', error);
            this.handleDataLoadError();
          },
        });

      this.legend = Object.entries(this.mapMonitorsSvg).map(
        ([type, { size, content }]) => ({
          label: `${type[0].toLocaleUpperCase()}${type.slice(1)}`,
          svg: {
            content: this.domSanitizer.bypassSecurityTrustHtml(
              content.replace(/transform="scale\(0\.25\)"/g, '')
            ),
            size,
          },
        })
      );
    });
  }

  public edit() {
    this.useEditTool();
  }

  public cancel() {
    this.usePreviewTool();
    this.refreshMonitors().pipe(takeUntil(this.destroyed$)).subscribe();
  }

  public save() {
    this.usePreviewTool();

    if (this.changedMonitorsPosition && this.areaId) {
      this.changedMonitorsPosition.find((x) => x.id === 5);
      const areaPositions: MonitorsPositionChanged = {
        areaId: this.areaId,
        positions: this.changedMonitorsPosition,
      };
      this.areaMapService
        .updateMonitorsPositions(areaPositions)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          () => {
            this.snackbarService.open(
              'green',
              'AREA.MAP.MESSAGES.MONITORS_POSITION_UPDATED'
            );
            this.refreshMonitors().pipe(takeUntil(this.destroyed$)).subscribe();
            this.changedMonitorsPosition = undefined;
          },
          () => {
            this.snackbarService.open(
              'red',
              'AREA.MAP.MESSAGES.MONITORS_POSITION_UPDATE_ERROR'
            );
          }
        );
    }
  }

  public changeMonitorsPosition(objects: PlacedObject[]): void {
    this.changedMonitorsPosition = objects.map((object) => {
      const monitorType = this.mapMonitors.find(
        (monitor) => monitor.id === object.id
      )?.data.type;

      const {
        data: { id, type = monitorType },
        coordinates,
      } = object as MonitorMapObject;

      return {
        id,
        type,
        coordinates,
      };
    });
  }

  public toggleLegend(): void {
    this.showLegend = !this.showLegend;
  }

  public monitorClicked(object: ObjectMetadata): void {
    if (
      this.blockClicks ||
      this.hasMoved ||
      this.isSwiping ||
      this.isMultiTouch
    ) {
      return;
    }
    this.pendingClick = { object, timestamp: Date.now() };
    return;
  }

  public showMapWrapper() {
    this.showMap = !this.showMap;
  }
  public showLegendWrapper() {
    this.showLegend = !this.showLegend;
  }

  public toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  public hasActiveFilters(): boolean {
    const formValue = this.form.value;
    return !!(
      formValue.moduleId ||
      formValue.instrumentType ||
      (formValue.toxic && formValue.toxic !== 'all') ||
      (formValue.temporary && formValue.temporary !== 'all') ||
      (formValue.outside && formValue.outside !== 'all') ||
      (formValue.checkStatus && formValue.checkStatus !== 'all') ||
      formValue.showUnscannedOnly
    );
  }

  public resetFilters(): void {
    this.form.patchValue({
      moduleId: '',
      instrumentType: '',
      toxic: 'all',
      temporary: 'all',
      outside: 'all',
      checkStatus: 'all',
      showUnscannedOnly: false,
    });

    localStorage.removeItem(`moduleId_${this.areaId}`);
    localStorage.removeItem(`instrumentType_${this.areaId}`);
    localStorage.removeItem(`toxicFilter_${this.areaId}`);
    localStorage.removeItem(`temporaryFilter_${this.areaId}`);
    localStorage.removeItem(`outsideFilter_${this.areaId}`);
    localStorage.removeItem(`unscannedOnly_${this.areaId}`);
    localStorage.removeItem(`checkStatusFilter_${this.areaId}`);

    this.filterMonitors('', '', 'all', 'all', 'all', false, 'all');

    if (
      this.areaDetailsMonitors &&
      this.areaDetailsMonitors.updateExternalFilters
    ) {
      this.areaDetailsMonitors.updateExternalFilters({
        moduleId: '',
        instrumentType: '',
        toxic: 'all',
        temporary: 'all',
        outside: 'all',
        showUnscannedOnly: false,
        checkStatus: 'all',
      });
    }
  }
  public startAddMonitor(): void {
    const sources = {
      instruments: this.dictionaryService
        .getDictionaryItemsByKeyV2('TABLE.PC.MONITOR.INSTRUMENTS')
        .pipe(catchError(() => of(null))),
      parts: this.getPartsForArea(this.areaId),
    };

    combineLatest(sources)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ instruments, parts }) => {
        let instrumentOptions: any[] = [];
        let partOptions: AreaContractPart[] = [];

        if (instruments) {
          instrumentOptions = instruments.Data.map((x) => ({
            label: this.options.monitor.types.find(
              (t) => t.value === x.instrumentId.toString()
            )?.label,
            instrumentId: x.instrumentId,
            moduleId: x.moduleId,
            value: x.value,
            token: this.options.monitor.types.find(
              (t) => t.value === x.instrumentId.toString()
            )?.token,
          }));
        }

        if (parts) {
          partOptions = parts;
        }

        const dialogData: AddMonitorDialogData = {
          areaId: this.areaId,
          instrumentOptions,
          partOptions,
          statusOptions: this.options.monitor.statuses.filter((status) =>
            ['1', '2'].includes(status.value)
          ),
          defaultCar: this.defaultCar,
          areaMapData: {
            backgroundSource: this.selectedArea?.map,
            objects: [...this.mapMonitors],
            objectSvg: { ...this.mapMonitorsSvg },
          },
          ...this.options.monitor.product,
        };

        const dialogRef = this.dialog.open<
          AddMonitorDialogComponent,
          AddMapActionDialogData,
          AddMonitorDialogResponse
        >(AddMonitorDialogComponent, {
          data: dialogData,
        });

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.destroyed$))
          .subscribe((result) => {
            result.startDate = new Date()
              .toLocaleString('sv')
              .replace(' ', 'T');
            if (result) {
              this.addMonitor(result);
            }
          });
      });
  }

  public startAddAction(): void {
    const dialogData: AddMapActionDialogData = {
      areaId: this.areaId,
      areaMapData: {
        objects: [...this.mapMonitors],
        objectSvg: { ...this.mapMonitorsSvg },
        backgroundSource: this.selectedArea?.map,
      },
    };

    const dialogRef = this.dialog.open<
      AddMapActionDialogComponent,
      AddMapActionDialogData,
      AddMapActionDialogResponse
    >(AddMapActionDialogComponent, {
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result) {
          this.addAction(result);
        }
      });
  }

  public addMonitor(monitor: AddMonitorDialogResponse) {
    if (monitor) {
      monitor.externalId = this.visitId;
      monitor.initialDate = new Date();

      this.areaMapService
        .addMonitor(monitor)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response: any) => {
            this.snackbarService.open(
              'green',
              'AREA.MAP.MESSAGES.MONITOR_ADDED_SUCCESSFULLY'
            );

            const monitorId = response?.id || response?.data?.id || response;
            if (monitorId) {
              this.refreshMonitors()
                .pipe(takeUntil(this.destroyed$))
                .subscribe(() => {
                  setTimeout(() => {
                    this.ngZone.run(() => {
                      this.router.navigate(
                        [
                          'area/monitor',
                          this.date,
                          this.visitId,
                          this.dayNumber,
                          this.areaId,
                          monitorId,
                        ],
                        {
                          state: {
                            backUrl: `/area/map/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`,
                            filteredMonitors: this.mappedMonitorsForList,
                          },
                        }
                      );
                    });
                  }, 100);
                });
            } else {
              this.refreshMonitors()
                .pipe(takeUntil(this.destroyed$))
                .subscribe();
            }
          },
          error: () =>
            this.snackbarService.open(
              'red',
              'AREA.MAP.MESSAGES.MONITOR_ADD_ERROR'
            ),
        });
    }
  }

  public addAction(action: AddMapActionDialogResponse) {
    if (action) {
      const payload = {
        ...action,
        customerId: this.customerId,
        locationId: this.locationId,
        parentObject: 'PC.Visit',
        parentId: this.visit.id,
        extended: true,
        mapX: action.mapCoordinates.x,
        mapY: action.mapCoordinates.y,
      };
      this.areaMapService
        .addAction(payload)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response: any) => {
            this.snackbarService.open(
              'green',
              'AREA.MAP.MESSAGES.ACTION_ADDED_SUCCESSFULLY'
            );

            const actionId = response?.id || response?.data?.id || response;
            if (actionId) {
              this.refreshMonitors()
                .pipe(takeUntil(this.destroyed$))
                .subscribe(() => {
                  setTimeout(() => {
                    this.router.navigate(['action'], {
                      state: {
                        backUrl: `/area/map/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`,
                        parentObject: 'PestControl.Area',
                        parentId: this.areaId,
                        customerId: this.customerId,
                        locationId: this.locationId,
                        areaId: this.areaId,
                        actionId,
                      },
                    });
                  }, 100);
                });
            } else {
              this.refreshMonitors()
                .pipe(takeUntil(this.destroyed$))
                .subscribe();
            }
          },
          error: () => () =>
            this.snackbarService.open(
              'red',
              'AREA.MAP.MESSAGES.ACTION_ADD_ERROR'
            ),
        });
    }
  }

  public refreshMonitors(): Observable<Monitor[]> {
    return this.areaApiService
      .getMonitors(this.areaId, this.visitId, {
        status: null,
        module: null,
        type: null,
      })
      .pipe(
        tap((areaMonitors) => {
          if (this.selectedArea) {
            this.selectedArea.monitors = areaMonitors;
          }

          const currentModuleId = this.form.get('moduleId')?.value;
          const currentInstrumentType = this.form.get('instrumentType')?.value;
          const currentToxic = this.form.get('toxic')?.value;
          const currentTemporary = this.form.get('temporary')?.value;
          const currentOutside = this.form.get('outside')?.value;
          const currentCheckStatus = this.form.get('checkStatus')?.value;
          const currentShowUnscannedOnly =
            this.form.get('showUnscannedOnly')?.value;

          localStorage.setItem(
            `moduleId_${this.areaId}`,
            currentModuleId || ''
          );
          localStorage.setItem(
            `instrumentType_${this.areaId}`,
            currentInstrumentType || ''
          );
          localStorage.setItem(
            `toxicFilter_${this.areaId}`,
            currentToxic || 'all'
          );
          localStorage.setItem(
            `temporaryFilter_${this.areaId}`,
            currentTemporary || 'all'
          );
          localStorage.setItem(
            `outsideFilter_${this.areaId}`,
            currentOutside || 'all'
          );
          localStorage.setItem(
            `unscannedOnly_${this.areaId}`,
            currentShowUnscannedOnly?.toString() || 'false'
          );

          this.filterMonitors(
            currentModuleId || '',
            currentInstrumentType || '',
            currentToxic || 'all',
            currentTemporary || 'all',
            currentOutside || 'all',
            currentShowUnscannedOnly || false,
            currentCheckStatus || 'all'
          );

          this.rebuildMapMonitorsSvgAndLegend();
        })
      );
  }

  public filterMonitors(
    moduleId: string,
    instrumentType: string,
    toxic: string,
    temporary: string,
    outside: string,
    showUnscannedOnly: boolean,
    checkStatus: string
  ): void {
    const hasCoordinates = (
      monitor: Monitor
    ): monitor is Required<Pick<Monitor, 'coordinates'>> & Monitor =>
      !!monitor.coordinates;

    const filteredMonitors = this.selectedArea?.monitors
      ?.filter(hasCoordinates)
      ?.filter((monitor) => {
        const moduleMatch = moduleId ? monitor.moduleId === moduleId : true;
        const typeMatch = instrumentType
          ? monitor.typeId === instrumentType
          : true;
        const toxicMatch =
          toxic === 'all'
            ? true
            : toxic === 'yes'
            ? monitor.toxic === true
            : monitor.toxic !== true;
        const temporaryMatch =
          temporary === 'all'
            ? true
            : temporary === 'yes'
            ? monitor.temporary === true
            : monitor.temporary !== true;
        const outsideMatch =
          outside === 'all'
            ? true
            : outside === 'yes'
            ? monitor.outside === true
            : monitor.outside !== true;
        const checkStatusMatch =
          checkStatus === 'all'
            ? true
            : checkStatus === 'yes'
            ? monitor.checkStatus === true
            : monitor.checkStatus !== true;

        const unscannedMatch = showUnscannedOnly
          ? monitor.observation?.id === 200
          : true;

        return (
          moduleMatch &&
          typeMatch &&
          toxicMatch &&
          temporaryMatch &&
          outsideMatch &&
          checkStatusMatch &&
          unscannedMatch
        );
      });
    this.mapMonitors = mapMonitorsToMapObjects(filteredMonitors);

    this.updateMappedMonitorsForList(this.selectedArea?.monitors || []);

    this.rebuildMapMonitorsSvgAndLegend();
  }

  public goBack() {
    this.router.navigate([
      '/area',
      this.date,
      this.visitId,
      this.dayNumber,
      this.areaId,
    ]);
  }

  public goToHome(): void {
    this.router.navigate(['/home']);
  }

  public updateDisabledStates(): void {
    this.isEditingDisabled = this.visitService.isEditingDisabled(this.visit);
  }

  public onObservationChange(event: any): void {
    this.refreshMonitors().pipe(takeUntil(this.destroyed$)).subscribe();
  }

  public onRefreshMonitorsData(monitorsData: Monitor[]): void {
    this.refreshMonitors().pipe(takeUntil(this.destroyed$)).subscribe();
  }

  public onSaveFields(): void {}

  public onFiltersChanged(filters: {
    moduleId?: string;
    instrumentType?: string;
    toxic: string;
    temporary: string;
    outside: string;
    showUnscannedOnly: boolean;
    checkStatus: string;
  }): void {
    this.form.patchValue(
      {
        moduleId: filters.moduleId || '',
        instrumentType: filters.instrumentType || '',
        toxic: filters.toxic,
        temporary: filters.temporary,
        outside: filters.outside,
        showUnscannedOnly: filters.showUnscannedOnly,
        checkStatus: filters.checkStatus,
      },
      { emitEvent: true }
    );
  }

  public getAreaMapBackUrl(): string {
    return `/area/map/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`;
  }

  ngOnDestroy() {
    this.backButtonSubscription?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();

    this.cleanupTouchHandlers();
  }

  private handleDataLoadError(): void {
    this.hasError = true;
    this.loading = false;
    this.selectedArea = undefined;

    this.snackbarService.open(
      'red',
      'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_UNABLE_TO_LOAD_DATA'
    );
  }

  private updateMappedMonitorsForList(monitorsData: Monitor[]): void {
    const monitorsOnly = monitorsData.filter(
      (monitor) => monitor.type === 'Monitor'
    );
    if (!this.options?.monitor) {
      this.mappedMonitorsForList = monitorsOnly.map((monitor) => ({
        ...monitor,
        moduleName: '',
        typeName: '',
        status: {
          name: '',
          statusId: monitor.statusId || '',
        },
        observation: monitor.observation || {
          id: 200,
          name: 'Unscanned',
          details: '',
        },
      }));
      return;
    }
    this.mappedMonitorsForList = monitorsOnly.map((monitor) => ({
      ...monitor,
      moduleName: this.getModuleName(monitor.moduleId || ''),
      typeName: this.getTypeName(monitor.typeId || ''),
      status: {
        name: this.getStatusName(monitor.statusId || ''),
        statusId: monitor.statusId || '',
      },
      observation: monitor.observation || {
        id: 200,
        name: 'Unscanned',
        details: '',
      },
    }));
  }

  private getModuleName(moduleId: string): string {
    if (!this.options?.monitor?.modules) {
      return '';
    }
    const module = this.options.monitor.modules.find(
      (m) => m.value === moduleId
    );
    return module?.label || '';
  }

  private getTypeName(typeId: string): string {
    if (!this.options?.monitor?.types) {
      return '';
    }
    const type = this.options.monitor.types.find((t) => t.value === typeId);
    return type?.label || '';
  }

  private getStatusName(statusId: string): string {
    if (!this.options?.monitor?.statuses) {
      return '';
    }
    const status = this.options.monitor.statuses.find(
      (s) => s.value === statusId
    );
    return status?.label || '';
  }

  private processPendingClick(): void {
    if (!this.pendingClick) {
      return;
    }

    const object = this.pendingClick.object;
    this.pendingClick = null;

    if (this.isTouching || this.blockClicks) {
      return;
    }

    if (this.hasMoved || this.isSwiping || this.isMultiTouch) {
      return;
    }

    const placed = object as PlacedObject<string, Monitor>;
    if (placed.data?.type === 'Monitor') {
      this.cleanupTouchHandlers();

      this.ngZone.run(() => {
        this.router.navigate(
          [
            'area/monitor',
            this.date,
            this.visitId,
            this.dayNumber,
            this.areaId,
            placed.id,
          ],
          {
            state: {
              backUrl: `/area/map/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`,
              filteredMonitors: this.mappedMonitorsForList,
            },
          }
        );
      });
    } else if (placed.data?.type === 'Action') {
      this.router.navigate(['action'], {
        state: {
          backUrl: `/area/map/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`,
          parentObject: 'PestControl.Area',
          parentId: this.areaId,
          customerId: this.customerId,
          locationId: this.locationId,
          areaId: this.areaId,
          actionId: placed.id ?? 0,
        },
      });
    }
  }

  private setupTouchHandling(): void {
    this.touchStartHandler = (event: TouchEvent) => {
      this.touchStartTime = Date.now();
      this.touchStartPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      this.isSwiping = false;
      this.hasMoved = false;
      this.isTouching = true;
      this.blockClicks = false;
      this.touchStartedOnMonitor = false;
      this.isMultiTouch = false;

      if (event.touches.length > 1) {
        this.isMultiTouch = true;
        this.blockClicks = true;
        return;
      }
      this.touchStartedOnMonitor = true;
    };

    this.touchMoveHandler = (event: TouchEvent) => {
      if (this.touchStartTime > 0) {
        if (event.touches.length > 1) {
          this.isMultiTouch = true;
          this.blockClicks = true;
          return;
        }

        const currentPosition = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };

        const distance = Math.sqrt(
          Math.pow(currentPosition.x - this.touchStartPosition.x, 2) +
            Math.pow(currentPosition.y - this.touchStartPosition.y, 2)
        );

        if (distance > this.swipeThreshold) {
          this.isSwiping = true;
          this.hasMoved = true;
          this.blockClicks = true;

          if (this.pendingClick) {
            this.pendingClick = null;
          }

          if (this.touchStartedOnMonitor) {
            this.addTimeout(() => {
              if (this.blockClicks && this.isSwiping) {
                this.blockClicks = false;
              }
            }, 800);
          }
        }
      }
    };

    this.touchEndHandler = (event: TouchEvent) => {
      this.touchEndPosition = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      };

      const touchDuration = Date.now() - this.touchStartTime;
      const distance = Math.sqrt(
        Math.pow(this.touchEndPosition.x - this.touchStartPosition.x, 2) +
          Math.pow(this.touchEndPosition.y - this.touchStartPosition.y, 2)
      );

      if (this.isMultiTouch) {
        this.pendingClick = null;
        this.blockClicks = true;
        this.addTimeout(() => {
          this.touchStartTime = 0;
          this.isTouching = false;
          this.isSwiping = false;
          this.hasMoved = false;
          this.blockClicks = false;
          this.touchStartedOnMonitor = false;
          this.isMultiTouch = false;
        }, 500);
        return;
      }

      if (this.isSwiping || this.hasMoved) {
        this.pendingClick = null;
        this.blockClicks = true;
        this.addTimeout(() => {
          this.touchStartTime = 0;
          this.isTouching = false;
          this.isSwiping = false;
          this.hasMoved = false;
          this.blockClicks = false;
          this.touchStartedOnMonitor = false;
        }, 500);
      } else {
        this.touchStartTime = 0;
        this.isTouching = false;
        this.blockClicks = false;
        this.touchStartedOnMonitor = false;

        if (this.pendingClick) {
          this.addTimeout(() => {
            this.processPendingClick();
          }, 150);
        }
      }
    };

    document.addEventListener('touchstart', this.touchStartHandler, {
      passive: false,
    });
    document.addEventListener('touchmove', this.touchMoveHandler, {
      passive: false,
    });
    document.addEventListener('touchend', this.touchEndHandler, {
      passive: false,
    });
  }

  private useEditTool() {
    this.tool = EditToolId;
    this.toolsService.useTool(this.tool);
  }

  private usePreviewTool() {
    this.tool = PreviewToolId;
    this.toolsService.useTool(this.tool);
  }

  private getPartsForArea(areaId: number): Observable<AreaContractPart[]> {
    return this.areaMapService.getPartsForArea(areaId, this.visitId).pipe(
      map((x) => x),
      catchError(() => of([]))
    );
  }
  private filterModuleOptions(selectedModules, allModuleOptions) {
    const moduleIds = selectedModules.map((module) => module.moduleId);
    return allModuleOptions.filter((option) =>
      moduleIds.includes(option.value)
    );
  }

  private filterInstrumentTypeOptions(selectedMonitors, allTypeOptions) {
    const typeIds = selectedMonitors.map((monitor) => monitor.typeId);
    return allTypeOptions.filter((option) => typeIds.includes(option.value));
  }

  private addTimeout(callback: () => void, delay: number): NodeJS.Timeout {
    const timeoutId = setTimeout(() => {
      callback();
      const index = this.pendingTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.pendingTimeouts.splice(index, 1);
      }
    }, delay);
    this.pendingTimeouts.push(timeoutId);
    return timeoutId;
  }

  private clearAllTimeouts(): void {
    this.pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.pendingTimeouts = [];
  }

  private async loadSelectedCarForToday(): Promise<void> {
    try {
      const currentDate = new Date();
      const dateKey = currentDate.toISOString().split('T')[0];
      const storageKey = `${dateKey}_selectedCar`;

      const todaysSelectedCar = await this.storageService.getObject(storageKey);

      if (todaysSelectedCar) {
        this.defaultCar =
          typeof todaysSelectedCar === 'string'
            ? parseInt(todaysSelectedCar, 10)
            : todaysSelectedCar;
      } else {
        this.defaultCar = this.stateService.getValue('defaultCar');
      }
    } catch (error) {
      this.defaultCar = this.stateService.getValue('defaultCar');
    }
  }

  private cleanupTouchHandlers(): void {
    if (this.touchStartHandler) {
      document.removeEventListener('touchstart', this.touchStartHandler);
      document.removeEventListener('touchmove', this.touchMoveHandler);
      document.removeEventListener('touchend', this.touchEndHandler);

      this.touchStartHandler = null;
      this.touchMoveHandler = null;
      this.touchEndHandler = null;
    }

    this.clearAllTimeouts();
  }

  private rebuildMapMonitorsSvgAndLegend() {
    const { monitorSvg, legend } = this.areaMapObjectsService.buildMonitorsSvg(
      this.mapMonitors,
      this.options
    );

    this.mapMonitorsSvg = monitorSvg;
    this.legend = legend;
  }
}
